import React, { useState } from 'react';
import { ActiveStatus } from '../../../../components';
import { TooltipIcon } from '../../../../components/Icons/TooltipIcon';
import { Role } from '../../../../types';
import { ConnectCrm } from '../connectCrmModal/ConnectCrmModal';
import { Toggle } from '../Toggle/Toggle';
import {
  appChromeIcon,
  appCrmIcon,
  appQChromeImg,
  appSalesforceImg,
} from './assets';

type Features = {
  xRay: boolean;
  eventNotifications: boolean;
  clusterEngagements: boolean;
};

type AppProps = {
  defaultActive?: boolean;
  title: string;
  text: string;
  role?: Role;
  name?: string;
  isQChrome?: boolean;
  enabledFeatures?: Features;
};

export const App: React.FC<AppProps> = ({
  defaultActive = false,
  title,
  text,
  role,
  name,
  isQChrome = false,
  enabledFeatures = {
    xRay: false,
    eventNotifications: false,
    clusterEngagements: false,
  },
}: AppProps): JSX.Element => {
  const [crmModalOpen, setCrmModalOpen] = useState(false);

  const [tooltipOpen, setTooltipOpen] = useState(false);

  const { xRay, eventNotifications, clusterEngagements } = enabledFeatures;

  const toggleCrmModal = () => {
    setCrmModalOpen(!crmModalOpen);
  };

  const tooltipToggle = () => {
    setTooltipOpen(!tooltipOpen);
  };

  const contactUsClick = () => {
    toggleCrmModal();
  };

  const attrs = {
    wrapper: {
      className: 'app',
    },

    body: {
      wrapper: {
        className: 'app__body',
      },

      preview: {
        wrapper: {
          className: 'app__body__preview',
        },

        centerImg: {
          className: 'app__body__preview-centered-img',
          src: isQChrome ? appQChromeImg : appSalesforceImg,
        },

        icon: {
          className: 'app__body__preview-item-icon',
          src: isQChrome ? appChromeIcon : appCrmIcon,
        },
      },

      info: {
        wrapper: {
          className: 'app__body__info',
        },

        title: {
          wrapper: {
            className: 'app__body__info__title',
          },

          label: {
            className: 'app__body__info__title-label',
          },

          activeElement: {
            wrapper: {
              className: 'app__body__info__title__active-element',
            },

            textForDealMaker: {
              className: 'app__body__info__title__active-element-text',
            },

            contactUs: {
              className: 'app__body__info__title__active-element-button',
              onClick: contactUsClick,
            },
          },
        },

        content: {
          wrapper: {
            className: 'app__body__info__content',
          },

          text: {
            className: 'app__body__info__content-text',
          },

          qSection: {
            wrapper: {
              className: 'app__body__info__content__q-section',
            },

            toggleRow: {
              wrapper: {
                className: 'app__body__info__content__q-section__toggle-row',
              },

              title: {
                className:
                  'app__body__info__content__q-section__toggle-row-title',
              },

              tooltip: {
                wrapper: {
                  className:
                    'app__body__info__content__q-section__toggle-row__tooltip',
                },

                icon: {
                  className:
                    'app__body__info__content__q-section__toggle-row__tooltip-icon',
                  active: tooltipOpen,
                  openTooltip: tooltipToggle,
                  closeTooltip: tooltipToggle,
                  isDarker: true,
                },

                text: {
                  className: `${
                    tooltipOpen ? 'open' : ''
                  } app__body__info__content__q-section__toggle-row__tooltip-text`,
                },
              },
            },
          },
        },
      },
    },

    link: {
      wrapper: {
        className: 'app__footer',
      },
    },
  };

  const emailAnalyzerStatus = defaultActive ? 'active' : 'inactive';

  const createToggleRow = (
    label: string,
    appName: string,
    isDefaultActive: boolean
  ) => {
    const tooltipText = `Substrata automatically recognizes new email threads 
      from people you engage with and groups them
      with other threads that include these people.`;

    const tooltip =
      appName === 'watch-contacts' ? (
        <div {...attrs.body.info.content.qSection.toggleRow.tooltip.wrapper}>
          <TooltipIcon
            {...attrs.body.info.content.qSection.toggleRow.tooltip.icon}
          />
          <div {...attrs.body.info.content.qSection.toggleRow.tooltip.text}>
            {tooltipText}
          </div>
        </div>
      ) : null;

    const row = (
      <div {...attrs.body.info.content.qSection.toggleRow.wrapper}>
        <div {...attrs.body.info.content.qSection.toggleRow.title}>
          {label} {tooltip}
        </div>
        {role !== 'dealmaker' ? (
          <Toggle
            serviceName={label}
            defaultActive={isDefaultActive}
            appName={appName}
            isSmaller
            disabled={emailAnalyzerStatus === 'inactive'}
          />
        ) : (
          <ActiveStatus
            status={isDefaultActive ? 'active' : 'inactive'}
            className="app__body__info__title-status"
          />
        )}
      </div>
    );

    return row;
  };

  const toggleRows = (
    <>
      {createToggleRow('X-Ray Mode', 'x-ray-mode', xRay)}
      {createToggleRow(
        'Event Notifications',
        'event-notifications',
        eventNotifications
      )}
      {createToggleRow(
        'Automatically Group New Threads',
        'watch-contacts',
        clusterEngagements
      )}
    </>
  );

  const qSection = isQChrome ? (
    <div {...attrs.body.info.content.qSection.wrapper}>{toggleRows}</div>
  ) : null;

  const toggleOrStatus =
    role !== 'dealmaker' ? (
      <Toggle
        serviceName={title}
        defaultActive={defaultActive}
        disabled={name !== 'email-analyzer'}
        appName={name}
      />
    ) : (
      <ActiveStatus
        status={emailAnalyzerStatus}
        className="app__body__info__title-status"
      />
    );

  const footerSection =
    name === 'email-analyzer' ? (
      <div {...attrs.link.wrapper}>
        <a
          target="_blank"
          rel="noopener noreferrer"
          className="app__footer__link"
          href="https://chrome.google.com/webstore/detail/substratas-q-for-email/okeemaalpjcfeggfnahbeoagboahbbka"
        >
          Chrome Web Store
        </a>
      </div>
    ) : null;

  const contactUsOrText =
    role === 'dealmaker' ? (
      <div {...attrs.body.info.title.activeElement.textForDealMaker}>
        Contact your administrator to connect your CRM
      </div>
    ) : (
      <div {...attrs.body.info.title.activeElement.contactUs}>Contact Us</div>
    );

  const conditionalTitleActiveItem = isQChrome
    ? toggleOrStatus
    : contactUsOrText;

  return (
    <div {...attrs.wrapper}>
      <div {...attrs.body.wrapper}>
        <div {...attrs.body.preview.wrapper}>
          <img {...attrs.body.preview.centerImg} alt="" />
          <img {...attrs.body.preview.icon} alt="" />
        </div>
        <div {...attrs.body.info.wrapper}>
          <div {...attrs.body.info.title.wrapper}>
            <div {...attrs.body.info.title.label}>{title}</div>
            <div {...attrs.body.info.title.activeElement.wrapper}>
              {conditionalTitleActiveItem}
            </div>
          </div>
          <div {...attrs.body.info.content.wrapper}>
            <div {...attrs.body.info.content.text}>
              {text} {footerSection}
            </div>
            {qSection}
          </div>
        </div>
      </div>

      <ConnectCrm open={crmModalOpen} onClose={toggleCrmModal} />
    </div>
  );
};
